import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toParams } from "@/lib/kpi";
import { X, CalendarDays, Clock, Euro, Target, AlertTriangle, Info } from "lucide-react";
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from "recharts";

const fmtEur = v => v == null || isNaN(v) ? "—" : Math.round(v).toLocaleString("fr-FR").replace(/\u202F/g, " ") + " €";
const fmtH = v => v == null || isNaN(v) ? "—" : Math.round(v).toLocaleString("fr-FR") + " h";
const fmtEurH = v => v == null || isNaN(v) ? "—" : v.toFixed(1).replace(".", ",") + " €/h";
const fmtPct = v => v == null || isNaN(v) ? "—" : Math.round(v * 100) + "%";
const fmtDate = d => d ? new Date(d + "T00:00:00").toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }) : "";

function Stat({ icon: Icon, label, value, sub, tone = "text-slate-900", testid }) {
  return (
    <div className="bg-slate-50 rounded-lg border border-slate-200 px-3 py-2 min-w-0" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[9px] font-semibold uppercase tracking-wider text-slate-500"><Icon className="w-3 h-3"/> {label}</div>
      <div className={`text-[15px] font-bold tabular-nums leading-tight mt-0.5 ${tone}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-500 truncate">{sub}</div>}
    </div>
  );
}

export function WeekDrillDrawer({ semaine, filters, onClose }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [tab, setTab] = useState("jours");

  useEffect(() => {
    setD(null); setErr(false);
    const params = { ...toParams({ drs: filters?.drs, secteurs: filters?.secteurs, corners: filters?.corners, mois: filters?.mois }), semaine };
    if (filters?.exercice) params.exercice = filters.exercice;
    api.get("/kpi/productivite/week-detail", { params }).then(r => setD(r.data)).catch(() => setErr(true));
  }, [semaine, filters]);

  useEffect(() => {
    const h = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onClose]);

  const cible = useMemo(() => {
    const cs = d?.corners || [];
    let num = 0, den = 0;
    cs.forEach(c => { if (c.heure > 0 && c.cible) { num += c.cible * c.heure; den += c.heure; } });
    return den ? num / den : 60;
  }, [d]);

  const chartData = useMemo(() => (d?.days || []).map(x => ({
    jour: `${(x.jour || "").slice(0, 3)} ${fmtDate(x.date)}`,
    "CA réalisé": Math.round(x.ca), "Objectif": Math.round(x.objectif),
    "Productivité est.": Math.round((x.productivite_est || 0) * 10) / 10,
  })), [d]);

  const dayCols = d?.days || [];
  const t = d?.totals || {};

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="week-drill-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}/>
      <div className="relative bg-white w-full max-w-3xl h-full shadow-2xl flex flex-col animate-in slide-in-from-right duration-200">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-blue-600 text-white grid place-items-center font-bold text-sm">{d?.label || `S${String(semaine).padStart(2, "0")}`}</div>
          <div className="flex-1 min-w-0">
            <h2 className="font-bold text-slate-900 text-sm">Détail jour par jour — Semaine {semaine}</h2>
            <p className="text-[11px] text-slate-500 flex items-center gap-1">
              <CalendarDays className="w-3 h-3"/>
              {d?.date_start ? `${fmtDate(d.date_start)} → ${fmtDate(d.date_end)}` : "—"}
              {d?.annees?.length ? ` · ${d.annees.join(", ")}` : ""} · {t.n_corners ?? "—"} corner(s)
            </p>
          </div>
          <button onClick={onClose} data-testid="week-drill-close" className="w-8 h-8 rounded-lg hover:bg-slate-100 grid place-items-center"><X className="w-4 h-4 text-slate-500"/></button>
        </div>

        {!d && !err && <div className="flex-1 grid place-items-center text-sm text-slate-500 animate-pulse">Chargement…</div>}
        {err && (
          <div className="flex-1 grid place-items-center text-center p-6">
            <div><AlertTriangle className="w-8 h-8 text-rose-500 mx-auto mb-2"/><div className="text-sm font-semibold text-slate-700">Impossible de charger le détail</div></div>
          </div>
        )}
        {d && !err && (
          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Stat icon={Euro} label="CA HT semaine" value={fmtEur(t.ca_ht)} testid="wd-stat-ca"/>
              <Stat icon={Clock} label="Heures" value={fmtH(t.heure)} testid="wd-stat-h"/>
              <Stat icon={Target} label="Productivité" value={fmtEurH(t.productivite)} sub={`Cible ${fmtEurH(cible)}`}
                    tone={t.productivite >= cible ? "text-emerald-700" : "text-rose-600"} testid="wd-stat-prod"/>
              <Stat icon={Target} label="Objectif CA" value={fmtPct(t.pct_obj)} sub={t.objectif ? `Obj. ${fmtEur(t.objectif)}` : "Pas d'objectif"}
                    tone={t.pct_obj == null ? "text-slate-500" : t.pct_obj >= 1 ? "text-emerald-700" : "text-amber-600"} testid="wd-stat-obj"/>
            </div>

            {dayCols.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-xs text-slate-500" data-testid="wd-empty">
                Aucune donnée CA journalière pour cette semaine sur le périmètre filtré.
              </div>
            ) : (
              <>
                <div className="bg-white rounded-xl border border-slate-200 p-3" data-testid="wd-chart">
                  <h3 className="font-bold text-slate-800 text-xs mb-2">CA réalisé vs objectif · productivité estimée</h3>
                  <div className="h-52">
                    <ResponsiveContainer>
                      <ComposedChart data={chartData} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9"/>
                        <XAxis dataKey="jour" tick={{ fontSize: 10, fill: "#64748B" }} axisLine={false} tickLine={false}/>
                        <YAxis yAxisId="l" tick={{ fontSize: 10, fill: "#64748B" }} axisLine={false} tickLine={false} width={55} tickFormatter={v => Math.round(v / 1000) + "k"}/>
                        <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10, fill: "#64748B" }} axisLine={false} tickLine={false} width={45}/>
                        <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 6, fontSize: 12 }}
                                 formatter={(v, n) => n === "Productivité est." ? fmtEurH(v) : fmtEur(v)}/>
                        <Legend wrapperStyle={{ fontSize: 11 }}/>
                        <Bar yAxisId="l" dataKey="CA réalisé" fill="#2563EB" radius={[4, 4, 0, 0]} barSize={18}/>
                        <Bar yAxisId="l" dataKey="Objectif" fill="#E2E8F0" radius={[4, 4, 0, 0]} barSize={18}/>
                        <Line yAxisId="r" type="monotone" dataKey="Productivité est." stroke="#F59E0B" strokeWidth={2} dot={{ r: 3 }}/>
                        <ReferenceLine yAxisId="r" y={Math.round(cible * 10) / 10} stroke="#16A34A" strokeDasharray="5 3"/>
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="flex bg-slate-100 rounded-lg p-0.5 w-fit">
                  {[{ k: "jours", l: "Par jour" }, { k: "corners", l: "Par corner" }].map(x => (
                    <button key={x.k} onClick={() => setTab(x.k)} data-testid={`wd-tab-${x.k}`}
                            className={`text-[11px] px-3 py-1 rounded-md font-semibold ${tab === x.k ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"}`}>{x.l}</button>
                  ))}
                </div>

                {tab === "jours" && (
                  <div className="rounded-xl border border-slate-200 overflow-hidden" data-testid="wd-table-jours">
                    <table className="w-full text-[11px]">
                      <thead className="bg-slate-50 text-slate-500 uppercase text-[9px] tracking-wider">
                        <tr><th className="text-left px-3 py-2">Jour</th><th className="text-right px-3 py-2">CA réalisé</th><th className="text-right px-3 py-2">Objectif</th><th className="text-right px-3 py-2">Écart</th><th className="text-right px-3 py-2">N-1</th><th className="text-right px-3 py-2">Corners ouverts</th><th className="text-right px-3 py-2">Heures est.</th><th className="text-right px-3 py-2">Prod. est.</th></tr>
                      </thead>
                      <tbody>
                        {dayCols.map(x => (
                          <tr key={x.date} className="border-t border-slate-100 hover:bg-slate-50/70" data-testid={`wd-row-${x.date}`}>
                            <td className="px-3 py-1.5 font-semibold text-slate-900">{x.jour} <span className="text-slate-400 font-normal">{fmtDate(x.date)}</span></td>
                            <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{fmtEur(x.ca)}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">{fmtEur(x.objectif)}</td>
                            <td className={`px-3 py-1.5 text-right tabular-nums font-semibold ${x.ecart_obj >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{x.objectif ? (x.ecart_obj >= 0 ? "+" : "") + fmtEur(x.ecart_obj) : "—"}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">{fmtEur(x.n_1)}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums">{x.n_corners_ouverts}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">{fmtH(x.heures_est)}</td>
                            <td className={`px-3 py-1.5 text-right tabular-nums font-bold ${x.productivite_est >= cible ? "text-emerald-700" : "text-rose-600"}`}>{fmtEurH(x.productivite_est)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {tab === "corners" && (
                  <div className="rounded-xl border border-slate-200 overflow-auto max-h-[420px]" data-testid="wd-table-corners">
                    <table className="w-full text-[11px] min-w-[640px]">
                      <thead className="bg-slate-50 text-slate-500 uppercase text-[9px] tracking-wider sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-2">Corner</th>
                          {dayCols.map(x => <th key={x.date} className="text-right px-2 py-2">{(x.jour || "").slice(0, 3)}</th>)}
                          <th className="text-right px-3 py-2">Heures</th><th className="text-right px-3 py-2">Prod.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {d.corners.map(c => (
                          <tr key={c.corner_key} className="border-t border-slate-100 hover:bg-slate-50/70">
                            <td className="px-3 py-1.5 font-semibold text-slate-900 truncate max-w-[180px]" title={c.corner}>{c.corner}</td>
                            {dayCols.map(x => {
                              const v = c.days?.[x.date]?.ca || 0;
                              return <td key={x.date} className={`px-2 py-1.5 text-right tabular-nums ${v ? "text-slate-800" : "text-slate-300"}`}>{v ? Math.round(v).toLocaleString("fr-FR") : "·"}</td>;
                            })}
                            <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">{fmtH(c.heure)}</td>
                            <td className={`px-3 py-1.5 text-right tabular-nums font-bold ${c.productivite >= (c.cible || cible) ? "text-emerald-700" : "text-rose-600"}`}>{fmtEurH(c.productivite)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}

            <div className="flex items-start gap-2 text-[10px] text-slate-500 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
              <Info className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5"/>
              <span>{d.note || "Les heures sont saisies à la semaine : la productivité journalière est une estimation (heures réparties sur les jours d'ouverture)."}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
